import * as React from 'react';
import { logger } from '@/lib/logger';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import type { Workspace } from '@/types/workspace';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth as useClerkAuth, useOrganization } from '@clerk/clerk-react';
import { refreshSupabaseRealtimeAuth } from '@/integrations/supabase/client';
let organizationServicePromise: Promise<typeof import('@/services/organizationService')> | null =
  null;
const getOrganizationService = () => {
  if (!organizationServicePromise) {
    organizationServicePromise = import('@/services/organizationService');
  }
  return organizationServicePromise;
};

interface WorkspaceContextValue {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  hasNoWorkspaces: boolean;
  setWorkspace: (workspaceId: string) => void;
  refetch: () => void;
}

const WorkspaceContext = React.createContext<WorkspaceContextValue | undefined>(undefined);

export const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  // Get auth info from Clerk
  const clerkAuth = useClerkAuth();
  const { organization } = useOrganization();

  // Determine auth state
  const user = clerkAuth?.userId ? { id: clerkAuth.userId } : null;
  const authLoading = !clerkAuth?.isLoaded;

  const { workspaces, isLoading: workspacesLoading, refetch } = useWorkspaces();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = React.useState<string | null>(() => {
    return localStorage.getItem('lm_selectedWorkspaceId');
  });
  const [creatingWorkspace, setCreatingWorkspace] = React.useState(false);
  const hasAttemptedCreation = React.useRef(false);

  // Combined loading state
  const isLoading = authLoading || workspacesLoading;
  const hasNoWorkspaces = !isLoading && workspaces.length === 0;

  // On logout, keep the last workspace ID in localStorage so it can be restored on next login.
  // We still reset in-memory state to avoid leaking context while logged out.
  React.useEffect(() => {
    // Do nothing on logout — we keep the last selected workspace in both state and localStorage
    // so it can be restored on next login.
    // Query hooks will automatically fail gracefully while unauthenticated.
    if (!user) {
      // no-op
    }
  }, [user]);

  // Reset attempted creation flag when user changes
  React.useEffect(() => {
    hasAttemptedCreation.current = false;
  }, [user?.id, organization?.id]);

  // When the active Clerk organization changes, the Supabase access token is
  // cached at module level (auth.ts), so REST/realtime requests keep sending the
  // PREVIOUS org's JWT and RLS returns the previous org's data until a full page
  // reload. Force a fresh token for the new org, then refetch every org-scoped
  // query. Skips the initial mount/hydration so it only reacts to real switches.
  const prevOrgIdRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    const orgId = organization?.id;
    if (prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== orgId) {
      logger.log('Active Clerk org changed; refreshing Supabase token + queries', {
        from: prevOrgIdRef.current,
        to: orgId,
      });
      void (async () => {
        try {
          await refreshSupabaseRealtimeAuth({ forceRefresh: true });
        } catch (err) {
          logger.warn('Failed to refresh Supabase token on org switch', err);
        } finally {
          queryClient.invalidateQueries();
        }
      })();
    }
    prevOrgIdRef.current = orgId;
  }, [organization?.id, queryClient]);

  // Track if we've already attempted to update mapping for this org
  const hasMappingUpdate = React.useRef<Set<string>>(new Set());

  // Log organization info and fix mapping if needed
  React.useEffect(() => {
    if (organization && clerkAuth?.userId) {
      logger.log('Current Clerk workspace:', {
        id: organization.id,
        name: organization.name,
        slug: organization.slug,
      });

      // Check if this organization name matches an existing workspace
      // that needs its mapping updated (only do this once per org)
      if (
        (organization.name === "Martin's Workspace" || organization.name === 'Test Workspace') &&
        !hasMappingUpdate.current.has(organization.id)
      ) {
        hasMappingUpdate.current.add(organization.id);
        logger.log(
          'Checking/updating Clerk org mapping for existing workspace:',
          organization.name,
        );
        getOrganizationService()
          .then(({ updateClerkOrgMapping }) =>
            updateClerkOrgMapping(organization.name, organization.id, clerkAuth.userId),
          )
          .then(success => {
            if (success) {
              logger.log('Org mapping verified/updated');
              // Don't invalidate queries here as it causes unnecessary re-renders
              // The workspaces are already fetched correctly
            }
          })
          .catch(error => {
            logger.error('Error updating org mapping:', error);
            // Remove from set so it can be retried later if needed
            hasMappingUpdate.current.delete(organization.id);
          });
      }
    }
  }, [organization?.id, organization?.name, clerkAuth?.userId]);

  // Create default workspace for Clerk users if they have none
  React.useEffect(() => {
    // Skip if loading
    if (authLoading || workspacesLoading) {
      return;
    }

    // Skip if no user
    if (!user) {
      return;
    }

    // Log current state for debugging
    logger.log('Workspace check:', {
      userId: user.id,
      orgId: organization?.id,
      orgName: organization?.name,
      workspacesCount: workspaces.length,
      workspaces: workspaces.map(w => ({ id: w.id, name: w.name })),
    });

    // Skip if user already has workspaces
    if (workspaces && workspaces.length > 0) {
      return;
    }

    // Skip if we've already attempted creation
    if (hasAttemptedCreation.current || creatingWorkspace) {
      return;
    }

    // IMPORTANT: Clerk organizations ARE our workspaces now
    // When users sign up, Clerk shows a modal to create their first organization
    // We don't need to create separate workspace records anymore
    // Projects use clerk_org_id directly for organization scoping

    if (clerkAuth?.userId && organization) {
      logger.log('User has Clerk organization (workspace):', {
        userId: clerkAuth.userId,
        orgId: organization.id,
        orgName: organization.name,
      });
      // No need to create legacy workspaces - Clerk org IS the workspace
    }
  }, [
    authLoading,
    workspacesLoading,
    user,
    organization,
    clerkAuth?.userId,
    clerkAuth?.user,
    queryClient,
  ]);

  // Ensure a workspace is selected once list is loaded
  React.useEffect(() => {
    if (!isLoading && workspaces.length > 0) {
      if (!selectedId || !workspaces.find(w => w.id === selectedId)) {
        logger.log('Selecting first workspace:', workspaces[0].id);
        setSelectedId(workspaces[0].id);
      }
    }
  }, [isLoading, workspaces, selectedId]);

  React.useEffect(() => {
    if (selectedId) {
      localStorage.setItem('lm_selectedWorkspaceId', selectedId);
    } else {
      localStorage.removeItem('lm_selectedWorkspaceId');
    }
  }, [selectedId]);

  const setWorkspace = (id: string) => {
    setSelectedId(id);
    // Invalidate all queries that depend on workspace context
    queryClient.invalidateQueries();
  };

  const currentWorkspace = selectedId ? (workspaces.find(w => w.id === selectedId) ?? null) : null;

  const value: WorkspaceContextValue = {
    workspaces,
    currentWorkspace,
    isLoading,
    hasNoWorkspaces,
    setWorkspace,
    refetch,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspaceContext = () => {
  const ctx = React.useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspaceContext must be used within WorkspaceProvider');
  return ctx;
};
